import type { CreateCompanyDraftRequest } from "@/services/CompanyOnboardingService";

// Paso 2 del alta de empresa: datos mínimos de la empresa titular de la suscripción. El resto (registro,
// domicilios, documentación, representantes) se completa en el paso 3, después del pago.

export interface CompanySubscriptionFormData
{
    businessName: string;
    taxId: string;
    incorporationDate: string;
    email: string;
    countryPrefix: string;
    phone: string;
}

export type CompanySubscriptionField = keyof CompanySubscriptionFormData;

// TODO: sustituir por los precios de la suscripción "IronIA Empresa" del catálogo de suscripciones
export const CompanyFirstYearPrice = 345;
export const CompanyRenewalPrice = 114.99;

export function getInitialCompanySubscriptionFormData(): CompanySubscriptionFormData
{
    return {
        businessName: "",
        taxId: "",
        incorporationDate: "",
        email: "",
        countryPrefix: "+34",
        phone: "",
    };
}

export function formatEuros(amount: number): string
{
    return new Intl.NumberFormat("es-ES", {style: "currency", currency: "EUR", minimumFractionDigits: 2}).format(amount);
}

// Letras de organización del CIF. Con P, Q, R, S, W y N el control es letra; con A, B, E y H es dígito;
// con el resto puede ser cualquiera de los dos. La E es la de las comunidades de bienes.
const CifPattern = /^([ABCDEFGHJNPQRSUVW])(\d{7})([0-9A-J])$/;
const CifControlLetters = "JABCDEFGHI";

/** Valida un CIF español (letra + 7 dígitos + control). */
export function isValidSpanishCif(value: string): boolean
{
    const cif = value.trim().toUpperCase().replace(/[\s-]/g, "");
    const match = CifPattern.exec(cif);
    if(!match)
    {
        return false;
    }

    const [, organization, digits, control] = match;
    let sum = 0;
    for(let i = 0; i < digits.length; i++)
    {
        const digit = Number(digits[i]);
        if(i % 2 === 0)
        {
            // Posiciones impares: se dobla y se suman las cifras del resultado
            const doubled = digit * 2;
            sum += Math.floor(doubled / 10) + (doubled % 10);
        }
        else
        {
            sum += digit;
        }
    }

    const controlDigit = (10 - (sum % 10)) % 10;
    const controlLetter = CifControlLetters[controlDigit];

    if("PQRSWN".includes(organization))
    {
        return control === controlLetter;
    }
    if("ABEH".includes(organization))
    {
        return control === String(controlDigit);
    }
    return control === String(controlDigit) || control === controlLetter;
}

const EmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function toStartOfDay(value: Date): Date
{
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/** Campos sin rellenar o con formato no válido. */
export function getInvalidCompanySubscriptionFields(data: CompanySubscriptionFormData, today: Date = new Date()): CompanySubscriptionField[]
{
    const invalid: CompanySubscriptionField[] = [];

    if(data.businessName.trim() === "")
    {
        invalid.push("businessName");
    }
    if(!isValidSpanishCif(data.taxId))
    {
        invalid.push("taxId");
    }
    const incorporationDate = data.incorporationDate ? new Date(`${data.incorporationDate}T00:00:00`) : null;
    if(!incorporationDate || Number.isNaN(incorporationDate.getTime()) || incorporationDate.getTime() > toStartOfDay(today).getTime())
    {
        invalid.push("incorporationDate");
    }
    if(!EmailPattern.test(data.email.trim()))
    {
        invalid.push("email");
    }
    if(data.countryPrefix.trim() === "")
    {
        invalid.push("countryPrefix");
    }
    if(!/^\d{6,12}$/.test(data.phone.replace(/\s+/g, "")))
    {
        invalid.push("phone");
    }

    return invalid;
}

/** Petición del borrador de empresa en el backend (CreateCompanyDraftDto). */
export function buildCreateCompanyDraftRequest(data: CompanySubscriptionFormData): CreateCompanyDraftRequest
{
    return {
        businessName: data.businessName.trim(),
        taxId: data.taxId.trim().toUpperCase().replace(/[\s-]/g, ""),
        incorporationDate: data.incorporationDate,
        email: data.email.trim(),
        countryPrefix: data.countryPrefix,
        phone: data.phone.replace(/\s+/g, ""),
    };
}

export function isCompanySubscriptionFormComplete(data: CompanySubscriptionFormData, today: Date = new Date()): boolean
{
    return getInvalidCompanySubscriptionFields(data, today).length === 0;
}
